import { useDocumentChangeListener } from "@/hooks/useDocumentChangeListener";
import { useDocumentCreationOnError } from "@/hooks/useDocumentCreationOnError";
import { useDocument } from "@/hooks/useDocument";
import * as React from "react";
import { LogOut, UserPlus } from "lucide-react";
import { useUser } from "@/hooks/useUser";
import { useLogout } from "@/hooks/useLogout";
import { getDisplayName, getInitials, getProfilePicture } from "@/utils/user.utils";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ErrorBoundary } from "./ErrorBoundary";
import { CopyDiagnosticsButton } from "./CopyDiagnosticsButton";
import { useSubscription } from "@/hooks/useSubscription";
import { Loader } from "lucide-react";
import { RequestGalleryProvider } from "./request-gallery/RequestGalleryContext";
import { IUser } from "@/types/auth.types";
import { useDocumentCleanup } from "@/taskpane/hooks/useDocumentCleanup";
import {
  AuthenticatedScreenTabs,
  useAuthenticatedTabs,
  usePaneLicenseGate,
} from "./AuthenticatedScreen.hooks";
import { getBackendDocumentId } from "@/utils/document-id.utils";

import Discuss from "./Discuss";
const Research = React.lazy(() => import("./Research"));
const Review = React.lazy(() => import("./Review"));
const UpgradePlanDialog = React.lazy(() => import("./UpgradePlanDialog"));
const ReferUserDialog = React.lazy(() => import("./ReferUserDialog"));
const RequestAccessDialog = React.lazy(() => import("./RequestAccessDialog"));

const UNREAD_DOT_CLASS =
  "absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full z-50 border border-background";

const TabSuspenseFallback: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-full gap-4">
    <Loader className="animate-spin" size={48} />
    <p>Loading...</p>
  </div>
);

const TopbarUserMenu: React.FC<{
  user: IUser | undefined;
  logoutMutation: ReturnType<typeof useLogout>;
  onReferClick: () => void;
}> = ({ user, logoutMutation, onReferClick }) => (
  <DropdownMenu>
    <DropdownMenuTrigger>
      <Avatar className="h-8 w-8">
        <AvatarImage
          src={getProfilePicture(user ?? null) ?? ""}
          alt={getDisplayName(user ?? null)}
        />
        <AvatarFallback>{getInitials(user ?? null)}</AvatarFallback>
      </Avatar>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" forceMount>
      <DropdownMenuItem onClick={onReferClick}>
        <UserPlus className="mr-2 h-4 w-4" />
        <span>Refer a Friend</span>
      </DropdownMenuItem>
      {/* Reachable even when nothing has visibly crashed — support can ask for
          one click instead of talking a customer through developer tools. */}
      <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="p-0">
        <CopyDiagnosticsButton
          variant="ghost"
          size="sm"
          className="w-full justify-start font-normal"
        />
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
        <LogOut className="mr-2 h-4 w-4" />
        <span>{logoutMutation.isPending ? "Logging out..." : "Logout"}</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const TAB_CONTENT_CLASS = "flex-1 min-h-0 flex flex-col";

const AuthenticatedTabPanels: React.FC<{
  selectedTab: AuthenticatedScreenTabs;
  handleNewMessage: () => void;
  handleResearchNewMessage: () => void;
  handleReviewComplete: () => void;
}> = ({ selectedTab, handleNewMessage, handleResearchNewMessage, handleReviewComplete }) => (
  <React.Suspense fallback={<TabSuspenseFallback />}>
    <TabsContent
      value={AuthenticatedScreenTabs.DISCUSS}
      className={TAB_CONTENT_CLASS}
      forceMount
      hidden={selectedTab !== AuthenticatedScreenTabs.DISCUSS}
    >
      {/* One boundary per tab: a failure in one must not take the other two —
          or the whole task pane — down with it. These sit ABOVE each tab's own
          render, so they also catch errors thrown in the tab's hooks/memos,
          which a boundary placed inside the tab cannot see. */}
      <ErrorBoundary area="discuss" title="Discuss couldn't load">
        <Discuss
          onNewMessage={handleNewMessage}
          isActive={selectedTab === AuthenticatedScreenTabs.DISCUSS}
        />
      </ErrorBoundary>
    </TabsContent>
    <TabsContent
      value={AuthenticatedScreenTabs.RESEARCH}
      className={TAB_CONTENT_CLASS}
      forceMount
      hidden={selectedTab !== AuthenticatedScreenTabs.RESEARCH}
    >
      <ErrorBoundary area="research" title="Research couldn't load">
        <Research
          onNewMessage={handleResearchNewMessage}
          isActive={selectedTab === AuthenticatedScreenTabs.RESEARCH}
        />
      </ErrorBoundary>
    </TabsContent>
    <TabsContent
      value={AuthenticatedScreenTabs.REVIEW}
      className={TAB_CONTENT_CLASS}
      forceMount
      hidden={selectedTab !== AuthenticatedScreenTabs.REVIEW}
    >
      <ErrorBoundary area="review" title="Review couldn't load">
        <Review onReviewComplete={handleReviewComplete} />
      </ErrorBoundary>
    </TabsContent>
  </React.Suspense>
);

const AuthenticatedTabsList: React.FC<{
  hasUnreadDiscuss: boolean;
  hasUnreadResearch: boolean;
  hasUnreadReview: boolean;
}> = ({ hasUnreadDiscuss, hasUnreadResearch, hasUnreadReview }) => (
  <TabsList className="grid w-full grid-cols-3 p-1.5 h-auto">
    <TabsTrigger className="h-9 text-xs relative" value={AuthenticatedScreenTabs.DISCUSS}>
      Discuss{hasUnreadDiscuss && <span className={UNREAD_DOT_CLASS} />}
    </TabsTrigger>
    <TabsTrigger className="h-9 text-xs relative" value={AuthenticatedScreenTabs.REVIEW}>
      Review{hasUnreadReview && <span className={UNREAD_DOT_CLASS} />}
    </TabsTrigger>
    <TabsTrigger className="h-9 text-xs relative" value={AuthenticatedScreenTabs.RESEARCH}>
      Research{hasUnreadResearch && <span className={UNREAD_DOT_CLASS} />}
    </TabsTrigger>
  </TabsList>
);

const AuthenticatedMainContent: React.FC<{
  isDisabled: boolean;
  user: IUser | undefined;
  logoutMutation: ReturnType<typeof useLogout>;
  selectedTab: AuthenticatedScreenTabs;
  handleTabChange: (value: string) => void;
  hasUnreadDiscuss: boolean;
  hasUnreadResearch: boolean;
  hasUnreadReview: boolean;
  handleNewMessage: () => void;
  handleResearchNewMessage: () => void;
  handleReviewComplete: () => void;
  onReferClick: () => void;
}> = ({
  isDisabled,
  user,
  logoutMutation,
  selectedTab,
  handleTabChange,
  hasUnreadDiscuss,
  hasUnreadResearch,
  hasUnreadReview,
  handleNewMessage,
  handleResearchNewMessage,
  handleReviewComplete,
  onReferClick,
}) => (
  <div
    className={`flex flex-col h-full bg-background ${isDisabled ? "pointer-events-none opacity-50" : ""}`}
  >
    <div className="flex items-center justify-between px-4 py-2 border-b">
      <div>
        <img src="assets/aila-logo-updated.png" alt="Logo" width="92" height="34" />
      </div>
      <div>
        <TopbarUserMenu user={user} logoutMutation={logoutMutation} onReferClick={onReferClick} />
      </div>
    </div>
    <div className="px-4 py-2 flex-1 overflow-hidden">
      <Tabs
        value={selectedTab}
        onValueChange={handleTabChange}
        className="w-full h-full flex flex-col gap-3"
      >
        <AuthenticatedTabsList
          hasUnreadDiscuss={hasUnreadDiscuss}
          hasUnreadResearch={hasUnreadResearch}
          hasUnreadReview={hasUnreadReview}
        />
        <AuthenticatedTabPanels
          selectedTab={selectedTab}
          handleNewMessage={handleNewMessage}
          handleResearchNewMessage={handleResearchNewMessage}
          handleReviewComplete={handleReviewComplete}
        />
      </Tabs>
    </div>
  </div>
);

const DialogSuspenseFallback: React.FC<{ isForbidden: boolean }> = ({ isForbidden }) =>
  isForbidden ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Loader className="animate-spin text-white" size={24} />
    </div>
  ) : null;

const AuthenticatedScreen: React.FC = () => {
  const { user } = useUser();
  const logoutMutation = useLogout();
  const { data: subscription } = useSubscription({ staleTime: 30000 });
  const { data: doc } = useDocument();
  const { showRequestAccess } = useDocumentCreationOnError();
  const [isReferUserDialogOpen, setIsReferUserDialogOpen] = React.useState(false);
  const tabs = useAuthenticatedTabs();
  const { isUnlicensed, isDisabled } = usePaneLicenseGate(
    subscription,
    showRequestAccess,
    getBackendDocumentId(doc)
  );

  useDocumentChangeListener();
  useDocumentCleanup({ uploadTextUrl: doc?.upload_text_url });

  return (
    <RequestGalleryProvider>
      <AuthenticatedMainContent
        isDisabled={isDisabled}
        user={user}
        logoutMutation={logoutMutation}
        selectedTab={tabs.selectedTab}
        handleTabChange={tabs.handleTabChange}
        hasUnreadDiscuss={tabs.hasUnreadDiscuss}
        hasUnreadResearch={tabs.hasUnreadResearch}
        hasUnreadReview={tabs.hasUnreadReview}
        handleNewMessage={tabs.handleNewMessage}
        handleResearchNewMessage={tabs.handleResearchNewMessage}
        handleReviewComplete={tabs.handleReviewComplete}
        onReferClick={() => setIsReferUserDialogOpen(true)}
      />
      <React.Suspense fallback={<DialogSuspenseFallback isForbidden={showRequestAccess} />}>
        <UpgradePlanDialog
          open={!!user && isUnlicensed && !showRequestAccess}
          onOpenChange={() => {}}
        />
        <ReferUserDialog open={isReferUserDialogOpen} onOpenChange={setIsReferUserDialogOpen} />
        <RequestAccessDialog open={showRequestAccess} onOpenChange={() => {}} />
      </React.Suspense>
    </RequestGalleryProvider>
  );
};

export default AuthenticatedScreen;
